
// @store/Tasks/TaskIssueComments/taskIssueCommentsSlice.ts
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../../API/axiosNeatex';

export interface TaskIssueComment {
  id: string;
  issueId: string;
  commentText: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  createdByName?: string;
}

// ==================== Thunks ====================

// جلب تعليقات قضية محددة
export const getIssueCommentsByIssueId = createAsyncThunk(
  'TaskIssueComments/getByIssueId',
  async (issueId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get(`Task/GetTaskIssueCommentsByIssueId?issueId=${issueId}`);
      if (data?.succeeded) return data.data;
      return [];
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

// إضافة تعليق
export const addIssueComment = createAsyncThunk(
  'TaskIssueComments/add',
  async (item: Omit<TaskIssueComment, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'createdByName'>,
    { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/AddTaskIssueComment', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في إضافة التعليق');
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

// تعديل تعليق
export const updateIssueComment = createAsyncThunk(
  'TaskIssueComments/update',
  async (item: TaskIssueComment, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/UpdateTaskIssueComment', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في تعديل التعليق');
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

// حذف تعليق
export const deleteIssueComment = createAsyncThunk(
  'TaskIssueComments/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post(`Task/DeleteTaskIssueComment?id=${id}`);
      if (data?.succeeded) return id;
      return rejectWithValue(data?.message || 'فشل في حذف التعليق');
    } catch (e: any) {
      return rejectWithValue(e.message);
    }
  }
);

// ==================== Slice ====================

interface IssueCommentsState {
  list: TaskIssueComment[];
  loading: boolean;
  error: string | null;
}

const initialState: IssueCommentsState = {
  list: [],
  loading: false,
  error: null
};

const TaskIssueCommentsSlice = createSlice({
  name: 'TaskIssueComments',
  initialState,
  reducers: {
    clearIssueComments: state => {
      state.list = [];
      state.loading = false;
      state.error = null;
    },
    addLocalIssueComment: (state, action: PayloadAction<TaskIssueComment>) => {
      state.list.unshift(action.payload);
    },
    updateLocalIssueComment: (state, action: PayloadAction<TaskIssueComment>) => {
      const i = state.list.findIndex(c => c.id === action.payload.id);
      if (i !== -1) state.list[i] = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getIssueCommentsByIssueId.pending, state => {
        state.loading = true; state.error = null;
      })
      .addCase(getIssueCommentsByIssueId.fulfilled, (state, action) => {
        state.loading = false; state.list = action.payload;
      })
      .addCase(getIssueCommentsByIssueId.rejected, (state, action) => {
        state.loading = false; state.error = action.payload as string;
      })

      .addCase(addIssueComment.fulfilled, (state, action) => {
        state.list.unshift(action.payload);
      })
      .addCase(addIssueComment.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      .addCase(updateIssueComment.fulfilled, (state, action) => {
        const i = state.list.findIndex(c => c.id === action.payload.id);
        if (i !== -1) state.list[i] = action.payload;
      })
      .addCase(updateIssueComment.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      .addCase(deleteIssueComment.fulfilled, (state, action) => {
        state.list = state.list.filter(c => c.id !== action.payload);
      })
      .addCase(deleteIssueComment.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

export const { clearIssueComments, addLocalIssueComment, updateLocalIssueComment } =
  TaskIssueCommentsSlice.actions;

export default TaskIssueCommentsSlice.reducer;
